import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  InputNumber,
  DatePicker,
  Input,
  Descriptions,
  Spin,
} from "antd";
import { toast } from "react-toastify";
import {
  createOutsourceJob,
  getVendorsByService,
} from "../../api/outsourceApi";
import { getAllJobs } from "../../api/jobApi";

const CreateOutsourceModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      fetchJobs();
      form.resetFields();
      setSelectedJob(null);
      setVendors([]);
    }
  }, [visible]);

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const data = await getAllJobs();
      setJobs(data.jobs || []);
    } catch (error) {
      console.error("Failed to load jobs", error);
    }
    setJobsLoading(false);
  };

  const handleJobChange = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    setSelectedJob(job);
    form.setFieldsValue({
      sent_quantity: job?.remaining_quantity || job?.quantity,
    });
  };

  const handleServiceTypeChange = async (serviceType) => {
    setVendorsLoading(true);
    form.setFieldsValue({ vendor_id: undefined });
    setVendors([]);
    try {
      const data = await getVendorsByService(serviceType);
      setVendors(Array.isArray(data) ? data : data.vendors || []);
    } catch (error) {
      console.error("Failed to load vendors for service", error);
    }
    setVendorsLoading(false);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        job_id: values.job_id,
        vendor_id: values.vendor_id,
        stage_id: values.stage_id || undefined,
        service_type: values.service_type,
        specifications: values.specifications
          ? JSON.parse(values.specifications)
          : undefined,
        sent_quantity: values.sent_quantity,
        expected_date: values.expected_date
          ? values.expected_date.format("YYYY-MM-DD")
          : undefined,
        quoted_rate: values.quoted_rate,
        notes: values.notes,
      };
      await createOutsourceJob(payload);
      toast.success("Outsource job created successfully");
      form.resetFields();
      setSelectedJob(null);
      onSuccess();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to create outsource job"
      );
    }
    setLoading(false);
  };

  return (
    <Modal
      title="Create Outsource Job"
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="job_id"
          label="Select Job"
          rules={[{ required: true, message: "Select a job" }]}
        >
          <Select
            showSearch
            placeholder="Search and select job"
            optionFilterProp="children"
            onChange={handleJobChange}
            loading={jobsLoading}
            notFoundContent={jobsLoading ? <Spin size="small" /> : undefined}
          >
            {jobs.map((job) => (
              <Select.Option key={job.id} value={job.id}>
                {job.job_number} - {job.customer?.name || ""} (
                {job.box_name || job.product_name || ""})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedJob && (
          <Descriptions
            size="small"
            column={2}
            bordered
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Box">
              {selectedJob.box_name || selectedJob.product_name}
            </Descriptions.Item>
            <Descriptions.Item label="Quantity">
              {selectedJob.quantity?.toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Size">
              {selectedJob.length}×{selectedJob.width}×{selectedJob.height}
            </Descriptions.Item>
            <Descriptions.Item label="Stage">
              {selectedJob.current_stage || "-"}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Form.Item
          name="service_type"
          label="Service Type"
          rules={[{ required: true, message: "Select service type" }]}
        >
          <Select
            placeholder="Select service type"
            onChange={handleServiceTypeChange}
          >
            <Select.Option value="printing">Printing</Select.Option>
            <Select.Option value="lamination">Lamination</Select.Option>
            <Select.Option value="casting">Casting</Select.Option>
            <Select.Option value="corrugation">Corrugation</Select.Option>
            <Select.Option value="pasting">Pasting</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="vendor_id"
          label="Vendor"
          rules={[{ required: true, message: "Select vendor" }]}
        >
          <Select
            placeholder={
              vendors.length === 0
                ? "Select a service type first"
                : "Select vendor"
            }
            loading={vendorsLoading}
            notFoundContent={
              vendorsLoading ? <Spin size="small" /> : "No vendors found"
            }
            disabled={vendors.length === 0 && !vendorsLoading}
          >
            {vendors.map((v) => (
              <Select.Option key={v.id} value={v.id}>
                {v.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="sent_quantity"
          label="Quantity to Send"
          rules={[{ required: true, message: "Enter quantity" }]}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={1}
            max={selectedJob?.quantity}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
            }
          />
        </Form.Item>

        <Form.Item name="expected_date" label="Expected Return Date">
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="quoted_rate" label="Quoted Rate (₹ per piece)">
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            precision={2}
            prefix="₹"
          />
        </Form.Item>

        <Form.Item name="specifications" label="Specifications (JSON)">
          <Input.TextArea
            rows={2}
            placeholder='e.g. {"colors": 4, "method": "offset"}'
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} placeholder="Special instructions..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateOutsourceModal;
